import type { FileRequirements, SingleValue, Language, FieldLimits } from '@eformulieren/engine'
import type {
  ContractRole,
  CriterionOutcome,
  DocumentVerwijzing,
  Field,
  VerwijzingOutcome,
} from '@eformulieren/model'
import type { ReactNode } from 'react'
import type * as Y from 'yjs'
import type { CitationField } from '../../citation/collectClusters.js'
import type { CitationResult } from '../../citation/types.js'
import type { FieldLink } from '../FieldShell.js'

/**
 * Wat elke control krijgt.
 *
 * **Presentatie en geen opslag.** Een control krijgt een waarde en geeft er een
 * terug; hij weet niets van Yjs, van een document of van een identiteit. Dat is
 * geen zuiverheid om de zuiverheid: het voorbeeld in de configurator gebruikt
 * dezelfde controls tegen een lokale stand (D-22, D-24), en de toets- en
 * nalezenmodi tekenen ze alleen-lezen. Eén control die zelf schrijft zou dat alles
 * aan de samenwerklaag vastknopen.
 *
 * De koppeling met label, hulptekst en melding komt van de veldschil (R-140); een
 * control zet die attributen niet zelf.
 *
 * **De vorm van de waarde is een gediscrimineerde unie op `vorm`**, met dezelfde
 * namen als `RESPONSE_SHAPE` in het model. De aanroeper leidt de vorm daaruit af, dus
 * de twee kunnen niet uiteenlopen: een control die een lijst waarden verwacht kan
 * geen enkele waarde binnenkrijgen zonder dat de typecheck of `requireValues` het
 * zegt.
 */

export interface Shared {
  readonly veld: Field
  readonly taal: Language
  readonly koppeling: FieldLink
  /** Uit het recht op de sectie en de laag die aan zet is (R-08, R-172). */
  readonly bewerkbaar: boolean
  /** De teken- en woordgrenzen uit de regels, voor de teller (R-12). */
  readonly limieten?: FieldLimits
}

/** Eén waarde: tekst, getal, datum, ja of nee. Leeg is leeg (geen antwoord). */
export interface SingleProps extends Shared {
  readonly vorm: 'waarde'
  readonly waarde: SingleValue | undefined
  readonly onWaarde: (value: SingleValue | undefined) => void
  /**
   * De stempel op een akkoordveld dat op Ja staat (R-19, R-89): wie het akkoord gaf en wanneer.
   *
   * Van de aanroeper en met de vergelijking al gedaan, want een control weet niets van een
   * identiteit (zie boven). Wat erin staat is de herkomst van het veld, en die schrijft de pod uit het token
   * en nooit uit invoer van de client (`apps/runtime/src/origin.ts`, R-154). Bij Nee of niet
   * aangeraakt geeft de aanroeper niets mee: dan is er geen akkoord en dus geen stempel. Alleen
   * `Toggle` leest dit, en alleen bij `akkoord`.
   */
  readonly stempel?: Stamp
  /**
   * Dit akkoord geeft de indiener en niet deze lezer (besluit van 3 november 2026, R-69, R-89): een
   * medeschrijver ziet het als leesweergave, met "Dit akkoord geeft de indiener" zolang het niet is
   * gegeven. Alleen `Toggle` leest dit, en alleen bij `akkoord`.
   */
  readonly doorIndiener?: boolean
}

/**
 * Wie een akkoord gaf en wanneer, zoals de invuller het te zien krijgt.
 *
 * Geen persoon en geen naam: alleen of het de lezer zelf was. Dit product kent geen naam (R-155e,
 * R-154), en een persoonsaanduiding als `portaal:<sleutel>` is geen naam maar een sleutel, die niet
 * op het scherm en niet bij een schermlezer hoort. Een echte naam is een open vraag aan NWO.
 */
export interface Stamp {
  /** Of de lezer zelf het akkoord gaf: "door u", anders "door een andere invuller". */
  readonly eigen: boolean
  /** Het moment in UTC, zoals het in de herkomst staat. */
  readonly moment: string
}

/**
 * Een verzameling waarden: de gekozen opties van een meerkeuzeveld of een
 * aanvinklijst, of de trefwoorden van een `tag` (R-15).
 *
 * Een lege lijst is een geldige stand: wie alles weer uitvinkt laat leeg achter.
 */
export interface ValuesProps extends Shared {
  readonly vorm: 'waarden'
  /**
   * Tekst of getal, en geen boolean. Dat volgt het model: een verzameling
   * ja-en-nee-waarden betekent niets, en een losse instemming is een `Waarde`
   * (`revisie.ts`). De engine kent `ChoiceValue` ruimer, omdat daar ook een enkele
   * waarde onder valt.
   */
  readonly waarden: readonly (string | number)[]
  readonly onValues: (values: readonly (string | number)[]) => void
}

/**
 * Rich text: het fragment zelf.
 *
 * De uitzondering op "een control krijgt een waarde en geeft er een terug", en met
 * een reden: bij rich text *is* het CRDT de waarde. Samen bewerken op tekenniveau
 * (R-72) kan niet over een tekstwaarde met een onChange, want dan gaat elke
 * gelijktijdige wijziging van een medeschrijver verloren bij het terugschrijven.
 *
 * Het fragment en niet het hele document: de control krijgt precies het stuk dat hij
 * mag schrijven, en niet de andere velden van de sectie.
 *
 * In de stand blijft rich text gewoon een `waarde` (`RESPONSE_SHAPE` in het model):
 * uitgeschreven als platte tekst, leesbaar zonder Yjs. De opslagvorm en de
 * antwoordvorm zijn twee verschillende dingen.
 */
export interface FragmentProps extends Shared {
  readonly vorm: 'fragment'
  readonly fragment: Y.XmlFragment
  /**
   * Er is iets veranderd door deze invuller zelf.
   *
   * Nodig omdat de tekst niet langs een `onWaarde` loopt: de samenwerklaag schrijft
   * hem. Twee dingen hangen eraan die anders zouden ontbreken: de herkomst per veld
   * (R-97, R-188) en het melden bij de opslag (R-163). Alleen bij een eigen
   * wijziging, want wie de tekst van een medeschrijver binnenkrijgt is niet degene
   * die hem schreef.
   */
  readonly onOwnChange?: () => void
}

/**
 * Een tekstveld (`text`, `textarea`): de `Y.Text` zelf (R-72, `MERGED_TEXT_CONTROLS`).
 *
 * Dezelfde uitzondering als bij rich text en om dezelfde reden: samenvoegen per teken kan niet over
 * een tekstwaarde met een `onWaarde`, want wie de hele tekst terugschrijft wist wat een
 * medeschrijver intussen typte. De control past alleen het verschil toe en zet de cursor recht als
 * er tekst van een ander binnenkomt (`useTextBinding`).
 *
 * In de stand blijft het een `waarde` met een gewone tekst (`RESPONSE_SHAPE` in het model).
 */
export interface TextProps extends Shared {
  readonly vorm: 'tekst'
  readonly tekst: Y.Text
  /**
   * Er is iets veranderd door deze invuller zelf; zie {@link FragmentProps}. Wordt binnen de
   * transactie van de wijziging aangeroepen, zodat tekst en herkomst samen binnenkomen.
   */
  readonly onOwnChange?: () => void
}

/**
 * Bijlagen: verwijzingen en nooit het bestand zelf (U25, R-199).
 *
 * Wat de control doorgeeft is een lijst `DocumentVerwijzing`: hash, bestandsnaam en
 * omvang. De bytes gaan langs `upload` naar de content-addressed opslag; zonder `upload`
 * berekent de browser de hash zelf en blijft het bestand daar.
 */
export interface DocumentsProps extends Shared {
  readonly vorm: 'documenten'
  readonly documenten: readonly DocumentVerwijzing[]
  readonly onDocuments: (documents: readonly DocumentVerwijzing[]) => void
  /** De bestandseisen uit de regels (R-104), voor het filter en de uitleg. */
  readonly requirements?: FileRequirements
  /**
   * Het bestand aanleveren bij de opslag, langs de pod (R-104, U25). Met deze functie komt een verwijzing
   * pas in de stand als de opslag het bestand aannam; zonder, zoals in de preview van de configurator,
   * rekent de browser alleen de hash uit, zoals vóór de opslag.
   */
  readonly upload?: (file: File) => Promise<UploadOutcome>
}

/** Wat een upload oplevert: de verwijzing, of de code waarmee de pod of de opslag weigerde. */
export type UploadOutcome = { readonly verwijzing: DocumentVerwijzing } | { readonly weigering: string }

/**
 * Een herhaalgroep: de lijst rijen uit het sectiedocument.
 *
 * De lijst en niet het hele document, om dezelfde reden als bij rich text: de
 * control krijgt precies de groep waar hij over gaat. Rijen zijn een `Y.Array` van
 * `Y.Map` met een stabiel rij-id (ontwerp 6.8), zodat twee mensen aan verschillende
 * rijen kunnen werken zonder elkaar te overschrijven (R-40 tot R-42).
 *
 * Het tekenen van een cel gaat via `renderRowField`, aangereikt door de aanroeper.
 * Zou de herhaalgroep de registry zelf aanroepen, dan verwijzen de registry en deze
 * control naar elkaar; en de aanroeper weet toch al hoe hij een veld tekent.
 */
export interface RowsProps extends Shared {
  readonly vorm: 'rijen'
  readonly lijst: Y.Array<Y.Map<unknown>>
  /** Hoe een cel in een rij wordt getekend. De rij is de map van die ene rij. */
  readonly renderRowField: (rowField: Field, row: Y.Map<unknown>, rowId: string) => ReactNode
  /** Het minimum en maximum aantal rijen uit de regels (R-42), voor de knoppen. */
  readonly counts?: { readonly minimum?: number; readonly maximum?: number }
}

/**
 * Een verwijzing: een alleen-lezen uitkomst uit het dossier (R-54, R-58, R-61, R-63).
 *
 * De zesde vorm, en de enige zonder `on...`-terugroep: niemand schrijft een verwijzing (R-56).
 * `bewerkbaar` uit `Gedeeld` doet hier dus niets, en dat is geen omissie maar de eis.
 *
 * De uitkomst komt van `resolveVerwijzingen` in de engine en draagt bij `onbeschikbaar` geen reden;
 * zie het besluit onder M6.0 in `docs/dekking.md` voor waarom dat verschil zelf informatie is.
 */
export interface VerwijzingProps extends Shared {
  readonly vorm: 'verwijzing'
  readonly uitkomst: VerwijzingOutcome
  /**
   * Waar het vandaan komt, in woorden voor de invuller (R-58).
   *
   * Aangereikt en niet uit `veld.bron` gehaald, want daar staat de bloknaam plus het pad en dat
   * is inrichting. Wat een invuller helpt is "de call"; wie dat label levert is de aanroeper, en
   * zolang de registry er geen aanlevert is de bloknaam het beste wat er is.
   */
  readonly bron?: string
  /**
   * De weergavenaam van het bronblok, als de schil die kent (R-58, R-125, ontwerp 9.5). De pod kent
   * alleen de naam van zijn eigen blok (`bloknaam` uit het image); voor een blok van buiten staat
   * hier niets en toont de control de blokverwijzing zelf.
   */
  readonly bronNaam?: string
  /** Onder een gedeelde bronregel (R-58): geen eigen pil en geen eigen bron bij dit veld. */
  readonly inBronGroep?: boolean
  /**
   * De rol per eigenschap van een reekselement (D-73), voor een `subgrid` op een reeks.
   *
   * Uit de bron van het veld, waar de publicatiepoort ze uit het contract heeft bevroren, of bij een
   * publicatie van daarvoor de terugval op de `criteria`-eigenschap (`seriesRolesFor` in
   * `shell/seriesRoles.ts`). Zonder `rollen`, of zonder een eigenschap met rol `vraag`, toont
   * `Subgrid` de platte tabel (D-73's eigen uitzondering).
   */
  readonly roles?: Readonly<Record<string, ContractRole>>
  /**
   * Het label per vraagwaarde, in de taal van de lezer, voor de titel van een groep (R-62, R-143).
   *
   * Een criterium-identiteit is voor een mens geen titel; het label van het criterium met die
   * identiteit op dit formulier wel (R-150). Een vraagwaarde zonder label houdt haar waarde.
   */
  readonly vraagLabels?: ReadonlyMap<string, string>
  /**
   * De vraagwaarden waarvan de groep al bij haar vraag staat, in de modus met historie (R-137).
   *
   * Die groepen staan hier niet nog een keer: dezelfde tabel twee keer is voor wie voorleest vooral
   * herhaling. Een groep zonder vraag op dit formulier blijft hier, want anders is ze nergens.
   */
  readonly elders?: ReadonlySet<string>
}

/**
 * Een criteriumuitkomst: oordeel, onderbouwing en de persoon die oordeelde (R-147, R-152).
 *
 * De zevende vorm, en de tweede die niet in de stand landt. Een criteriumuitkomst staat naast de
 * antwoorden en niet ertussen (U23, `RESPONSE_SHAPE` in het model zegt `laag`), want een aanvraag
 * moet te bevriezen zijn terwijl er nog aan de uitkomsten wordt geschreven.
 *
 * **`onVerdict` en niet `onWaarde`, en dat is geen naamsverschil.** Een oordeel is er in één keer
 * en precies één keer per criterium per instantie (U21, R-192): het gaat niet per toetsaanslag
 * naar de opslag zoals een antwoord, maar als geheel, met zijn verplichte onderbouwing erbij
 * (R-147). Een half oordeel bestaat niet, dus een tussentijdse `onWaarde` zou een vorm beloven
 * die de opslag weigert.
 *
 * **Waar het klad blijft is nog niet besloten**, en de control houdt het daarom in zijn eigen
 * stand tot het oordeel wordt gegeven. Dat is een tekortkoming en geen ontwerp: een lange
 * onderbouwing die bij het herladen weg is, is precies het faalbeeld dat valkuil 6 beschrijft.
 * Het besluit erover hangt aan dezelfde vraag als de stand waarop een oordeel rust; zie
 * `docs/dekking.md` bij M7 opdracht 2 en 3.
 */
export interface LaagProps extends Shared {
  readonly vorm: 'laag'
  /** Wat er al is: het oordeel als het is gegeven, en wat de machine vaststelde (R-180). */
  readonly uitkomst?: CriterionOutcome
  /** Het oordeel vellen. Eenmalig; daarna staat het er alleen-lezen. */
  readonly onVerdict: (verdict: Issued) => void | Promise<void>
}

/** Wat de invuller van een criterium invult; de rest vult de pod (R-154, R-175). */
export interface Issued {
  readonly oordeel: string | number
  readonly voldoet: boolean
  readonly onderbouwing: string
}

/**
 * Een vast component: het hele sectiedocument, want zijn inhoud groeit uit meer dan zijn eigen
 * veld-id (R-50). Zoals `verwijzing` al doet: `RESPONSE_SHAPE` van `component` is `geen`, en toch
 * heeft de control zijn eigen vorm en niet de generieke enkelwaarde.
 *
 * Alleen wat een vast component tot dusver nodig heeft: het document, om de bronnenopslag en de
 * rich-text-fragmenten te lezen, en de rich-text-veld-id's van deze sectie, voor de
 * citatie-engine (`packages/renderer/src/citatie/`). Geen schrijfpad: een vast component schrijft
 * vandaag niets terug.
 */
export interface SectionProps extends Shared {
  readonly vorm: 'sectie'
  readonly doc: Y.Doc
  /** Waar in de sectie citaties staan: rich-text-velden en rich-text-kolommen van herhaalgroepen. */
  readonly richtextVeldIds: readonly CitationField[]
  /**
   * De citatie-uitkomst van de laag van deze sectie, over de secties in de projectie, als de schil
   * die al berekent (`useSectionCitations`): dan
   * komen de literatuurlijst en de labels in de tekst uit dezelfde berekening. Zonder rekent het
   * component zelf, met dezelfde engine.
   */
  readonly citations?: CitationResult
}

/** Eén gegeven akkoord per persoon zoals de lijst het toont (eisenboek 12.56). */
export interface ConsentLine {
  /** Om te sleutelen, niet om te tonen: de persoonsaanduiding komt nooit op het scherm. */
  readonly sleutel: string
  /** Het e-mailadres uit het token, als naam (besluit van de product owner van 3 november 2026). */
  readonly naam: string
  readonly eigen: boolean
  readonly gegevenOp: string
  /** Of het akkoord nog over de inhoud van nu gaat. */
  readonly geldig: boolean
  /**
   * Of het akkoord namens een ander is gegeven, door een gemachtigde (R-190, R-191). Het product kent van
   * die ander alleen de persoonsaanduiding, en die komt niet op het scherm; daarom "namens de aanvrager".
   */
  readonly namens: boolean
}

/**
 * Een akkoord per persoon (eisenboek 12.56): wie akkoord gaf, en of de lezer zelf kan geven of intrekken.
 * Geen waarde om te schrijven: geven en intrekken vraagt de control de pod, en de pod schrijft.
 */
export interface ConsentProps extends Shared {
  readonly vorm: 'akkoorden'
  readonly akkoorden: readonly ConsentLine[]
  /** Het eigen akkoord van de lezer, als hij er een gaf. */
  readonly eigen?: { readonly geldig: boolean }
  /** Of de lezer akkoord kan geven: hij mag de sectie bewerken. */
  readonly magGeven: boolean
  readonly onGeven?: () => Promise<void>
  readonly onIntrekken?: () => Promise<void>
}

export type ControlProps =
  | ConsentProps
  | SingleProps
  | ValuesProps
  | FragmentProps
  | TextProps
  | DocumentsProps
  | RowsProps
  | VerwijzingProps
  | LaagProps
  | SectionProps

export function requireSingle(props: ControlProps): SingleProps {
  if (props.vorm !== 'waarde') {
    throw new Error(`${props.veld.control} verwacht één waarde, kreeg vorm ${props.vorm}`)
  }
  return props
}

export function requireConsents(props: ControlProps): ConsentProps {
  if (props.vorm !== 'akkoorden') {
    throw new Error(`${props.veld.control} verwacht akkoorden, kreeg vorm ${props.vorm}`)
  }
  return props
}

export function requireRows(props: ControlProps): RowsProps {
  if (props.vorm !== 'rijen') {
    throw new Error(`${props.veld.control} verwacht rijen, kreeg vorm ${props.vorm}`)
  }
  return props
}

export function requireDocuments(props: ControlProps): DocumentsProps {
  if (props.vorm !== 'documenten') {
    throw new Error(`${props.veld.control} verwacht verwijzingen, kreeg vorm ${props.vorm}`)
  }
  return props
}

export function requireFragment(props: ControlProps): FragmentProps {
  if (props.vorm !== 'fragment') {
    throw new Error(`${props.veld.control} verwacht een fragment, kreeg vorm ${props.vorm}`)
  }
  return props
}

/**
 * Een tekst uit het document, of een losse waarde.
 *
 * In het formulier krijgen `text` en `textarea` altijd hun `Y.Text` (R-72). De waardevorm blijft
 * toegestaan voor wie de control zonder document tekent, en dat is geen open deur: een andere vorm
 * gooit nog steeds, zoals bij de andere `require...`.
 */
export function requireSingleOrText(props: ControlProps): SingleProps | TextProps {
  if (props.vorm !== 'tekst' && props.vorm !== 'waarde') {
    throw new Error(`${props.veld.control} verwacht een tekst of één waarde, kreeg vorm ${props.vorm}`)
  }
  return props
}

export function requireValues(props: ControlProps): ValuesProps {
  if (props.vorm !== 'waarden') {
    throw new Error(`${props.veld.control} verwacht een lijst waarden, kreeg vorm ${props.vorm}`)
  }
  return props
}

export function requireVerwijzing(props: ControlProps): VerwijzingProps {
  if (props.vorm !== 'verwijzing') {
    throw new Error(`${props.veld.control} verwacht een verwijzingsuitkomst, kreeg vorm ${props.vorm}`)
  }
  return props
}

export function requireLaag(props: ControlProps): LaagProps {
  if (props.vorm !== 'laag') {
    throw new Error(`${props.veld.control} verwacht een criteriumuitkomst, kreeg vorm ${props.vorm}`)
  }
  return props
}

export function requireSection(props: ControlProps): SectionProps {
  if (props.vorm !== 'sectie') {
    throw new Error(`${props.veld.control} verwacht het sectiedocument, kreeg vorm ${props.vorm}`)
  }
  return props
}

/** De tekst van een waarde, of leeg. Een control werkt met tekst in het invoerveld. */
export function asText(value: SingleValue | undefined): string {
  if (value === undefined || value === null) return ''
  return String(value)
}
